<template>
  <div class="settings-view">
    <!-- Edit Company Modal -->
    <EditCompanyModal
      :show="showEditModal"
      :company="company"
      @close="showEditModal = false"
      @company-updated="onCompanyUpdated"
    />

    <!-- Company Settings Modal -->
    <CompanySettingsModal
      :show="showSettingsModal"
      @close="showSettingsModal = false"
      @settings-updated="onSettingsUpdated"
    />

    <!-- Logo Upload Modal -->
    <BaseModal
      v-model="showLogoUploadModal"
      title="Modifier le logo"
      size="md"
      :closable="!isUploadingLogo"
      :close-on-backdrop="!isUploadingLogo"
      @close="closeLogoUploadModal"
    >
      <div class="logo-upload-content">
        <!-- Preview Section -->
        <div class="logo-preview-section">
          <label class="preview-label">Aperçu</label>
          <div class="preview-container">
            <div v-if="logoPreview" class="preview-image-wrapper">
              <img :src="logoPreview" alt="Aperçu du logo" class="preview-image" />
            </div>
            <div v-else-if="company?.logo_url" class="preview-image-wrapper">
              <img :src="company.logo_url" alt="Logo actuel" class="preview-image" />
            </div>
            <div v-else class="preview-placeholder">
              <i class="fa-solid fa-image text-4xl text-gray-400"></i>
              <p class="text-gray-500 text-sm mt-2">Aucun logo</p>
            </div>
          </div>
        </div>

        <!-- File Input -->
        <div class="file-input-section">
          <label class="file-input-label">
            <input
              type="file"
              ref="fileInput"
              accept="image/*"
              @change="handleFileSelect"
              class="file-input"
              :disabled="isUploadingLogo"
            />
            <div class="file-input-button">
              <i class="fa-solid fa-upload"></i>
              <span>{{ selectedFile ? 'Changer le fichier' : 'Choisir un fichier' }}</span>
            </div>
            <p class="file-input-hint">Formats acceptés: JPG, PNG, GIF (max 2MB)</p>
          </label>
        </div>

        <!-- Error Message -->
        <div v-if="logoUploadError" class="error-message">
          <i class="fa-solid fa-exclamation-circle"></i>
          <span>{{ logoUploadError }}</span>
        </div>
      </div>

      <template #footer>
        <div class="flex justify-end gap-3 w-full">
          <BaseButton
            variant="ghost"
            :disabled="isUploadingLogo"
            @click="closeLogoUploadModal"
          >
            Annuler
          </BaseButton>
          <BaseButton
            variant="primary"
            :loading="isUploadingLogo"
            :disabled="!selectedFile"
            @click="uploadLogo"
          >
            Enregistrer
          </BaseButton>
        </div>
      </template>
    </BaseModal>

    <div class="page-header">
      <div class="header-info">
        <h1 class="page-heading">Paramètres</h1>
        <p class="page-subtitle">Configuration de votre entreprise</p>
      </div>
    </div>

    <div class="content-layout">
      <!-- Company Information Card -->
      <div class="card company-card">
        <div class="card-header">
          <div class="header-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M3 21h18"></path>
              <path d="M5 21V7l8-4v18"></path>
              <path d="M19 21V11l-6-4"></path>
              <path d="M9 9v.01"></path>
              <path d="M9 12v.01"></path>
              <path d="M9 15v.01"></path>
              <path d="M9 18v.01"></path>
            </svg>
          </div>
          <div class="header-text">
            <h3>Informations de l'entreprise</h3>
            <p>Détails et coordonnées de votre société</p>
          </div>
          <button @click="editCompany" class="action-btn secondary">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
            </svg>
            Modifier
          </button>
        </div>

        <div v-if="loading" class="loading-container">
          <div class="spinner"></div>
          <p>Chargement des informations...</p>
        </div>

        <div v-else-if="!company" class="empty-state">
          <div class="empty-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          </div>
          <h4>Aucune information</h4>
          <p>Les informations de l'entreprise ne sont pas disponibles</p>
        </div>

        <div v-else class="company-content">
          <!-- Company Profile Section -->
          <div class="profile-section">
            <div v-if="company.logo_url" class="profile-logo logo-container">
              <img :src="company.logo_url" :alt="company.name" />
              <button @click="openLogoUploadModal" class="logo-edit-btn" title="Modifier le logo">
                <i class="fa-solid fa-pencil"></i>
              </button>
            </div>
            <div v-else class="profile-avatar logo-container">
              {{ getInitials(company.name) }}
              <button @click="openLogoUploadModal" class="logo-edit-btn" title="Ajouter un logo">
                <i class="fa-solid fa-plus"></i>
              </button>
            </div>
            <div class="profile-info">
              <h2 class="company-name">{{ company.name || 'Non défini' }}</h2>
              <div v-if="company.business_type" class="company-type">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M20 7h-9"></path>
                  <path d="M14 17H5"></path>
                  <circle cx="17" cy="17" r="3"></circle>
                  <circle cx="7" cy="7" r="3"></circle>
                </svg>
                <span>{{ company.business_type }}</span>
              </div>
              <div class="company-meta">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                <span>Créé le {{ formatDate(company.created_at) }}</span>
              </div>
            </div>
          </div>

          <!-- Description Section -->
          <div v-if="company.description" class="description-section">
            <div class="section-header">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
              <span>Description</span>
            </div>
            <p class="description-text">{{ company.description }}</p>
          </div>

          <!-- Info Grid -->
          <div class="info-grid">
            <!-- Contact Info -->
            <div class="info-section">
              <div class="section-header">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                </svg>
                <span>Contact</span>
              </div>

              <div class="info-item" v-if="company.phone">
                <div class="info-icon phone">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                </div>
                <div class="info-details">
                  <span class="info-label">Téléphone</span>
                  <span class="info-value">{{ company.phone }}</span>
                </div>
              </div>

              <div class="info-item" v-if="company.email">
                <div class="info-icon email">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                </div>
                <div class="info-details">
                  <span class="info-label">Email</span>
                  <span class="info-value">{{ company.email }}</span>
                </div>
              </div>

              <div class="info-item" v-if="company.website">
                <div class="info-icon website">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="2" y1="12" x2="22" y2="12"></line>
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                  </svg>
                </div>
                <div class="info-details">
                  <span class="info-label">Site web</span>
                  <span class="info-value">{{ company.website }}</span>
                </div>
              </div>

              <div class="no-info" v-if="!company.phone && !company.email && !company.website">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <p>Aucune information de contact</p>
              </div>
            </div>

            <!-- Address Info -->
            <div class="info-section">
              <div class="section-header">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
                <span>Adresse</span>
              </div>

              <div class="info-item" v-if="company.address">
                <div class="info-icon address">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                </div>
                <div class="info-details">
                  <span class="info-label">Adresse</span>
                  <span class="info-value">{{ company.address }}</span>
                </div>
              </div>

              <div class="info-item" v-if="company.city">
                <div class="info-icon city">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M3 21h18"></path>
                    <path d="M9 21V10a3 3 0 0 1 6 0v11"></path>
                    <path d="M4 21V8l4-3 4 3v13"></path>
                    <path d="M16 21V4l4 3v14"></path>
                  </svg>
                </div>
                <div class="info-details">
                  <span class="info-label">Ville</span>
                  <span class="info-value">{{ company.city }}</span>
                </div>
              </div>

              <div class="info-item" v-if="company.country">
                <div class="info-icon country">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="2" y1="12" x2="22" y2="12"></line>
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                  </svg>
                </div>
                <div class="info-details">
                  <span class="info-label">Pays</span>
                  <span class="info-value">{{ company.country }}</span>
                </div>
              </div>

              <div class="no-info" v-if="!company.address && !company.city && !company.country">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <p>Aucune adresse renseignée</p>
              </div>
            </div>

            <!-- Legal Info -->
            <div class="info-section">
              <div class="section-header">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                </svg>
                <span>Informations légales</span>
              </div>

              <div class="info-item" v-if="company.ninea">
                <div class="info-icon legal">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                  </svg>
                </div>
                <div class="info-details">
                  <span class="info-label">NINEA</span>
                  <span class="info-value">{{ company.ninea }}</span>
                </div>
              </div>

              <div class="info-item" v-if="company.rccm">
                <div class="info-icon legal">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                  </svg>
                </div>
                <div class="info-details">
                  <span class="info-label">RCCM</span>
                  <span class="info-value">{{ company.rccm }}</span>
                </div>
              </div>

              <div class="info-item" v-if="company.tax_id">
                <div class="info-icon tax">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                    <line x1="1" y1="10" x2="23" y2="10"></line>
                  </svg>
                </div>
                <div class="info-details">
                  <span class="info-label">Numéro fiscal</span>
                  <span class="info-value">{{ company.tax_id }}</span>
                </div>
              </div>

              <div class="info-item" v-if="company.capital">
                <div class="info-icon capital">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <line x1="12" y1="1" x2="12" y2="23"></line>
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                  </svg>
                </div>
                <div class="info-details">
                  <span class="info-label">Capital</span>
                  <span class="info-value">{{ formatCurrency(company.capital) }}</span>
                </div>
              </div>

              <div class="no-info" v-if="!company.ninea && !company.rccm && !company.tax_id && !company.capital">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <p>Aucune information légale</p>
              </div>
            </div>

            <!-- Cash Register Info -->
            <div class="info-section">
              <div class="section-header">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                </svg>
                <span>Configuration caisse</span>
              </div>

              <div class="info-item" v-if="company.currency">
                <div class="info-icon currency">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="16"></line>
                    <line x1="8" y1="12" x2="16" y2="12"></line>
                  </svg>
                </div>
                <div class="info-details">
                  <span class="info-label">Devise</span>
                  <span class="info-value">{{ company.currency }}</span>
                </div>
              </div>

              <div class="info-item" v-if="company.cash_register_start_balance !== null && company.cash_register_start_balance !== undefined">
                <div class="info-icon balance">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <line x1="12" y1="1" x2="12" y2="23"></line>
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                  </svg>
                </div>
                <div class="info-details">
                  <span class="info-label">Solde de départ caisse</span>
                  <span class="info-value">{{ formatCurrency(company.cash_register_start_balance, company.currency) }}</span>
                </div>
              </div>

              <div class="info-item" v-if="company.max_cash_registers !== null && company.max_cash_registers !== undefined">
                <div class="info-icon registers">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                    <line x1="8" y1="21" x2="16" y2="21"></line>
                    <line x1="12" y1="17" x2="12" y2="21"></line>
                  </svg>
                </div>
                <div class="info-details">
                  <span class="info-label">Nombre max de caisses</span>
                  <span class="info-value">{{ company.max_cash_registers || 'Illimité' }}</span>
                </div>
              </div>

              <div class="no-info" v-if="!company.currency && (company.cash_register_start_balance === null || company.cash_register_start_balance === undefined) && (company.max_cash_registers === null || company.max_cash_registers === undefined)">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <p>Aucune configuration de caisse</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Advanced Settings Card -->
      <div class="card settings-card">
        <div class="card-header">
          <div class="header-icon settings-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="12" cy="12" r="3"></circle>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
            </svg>
          </div>
          <div class="header-text">
            <h3>Configuration avancée</h3>
            <p>Paramètres de vente et gestion des caisses</p>
          </div>
          <button @click="openSettingsModal" class="action-btn secondary">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="12" cy="12" r="3"></circle>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
            </svg>
            Configurer
          </button>
        </div>

        <div class="settings-content">
          <div class="settings-list">
            <div class="settings-item">
              <div class="settings-item-icon sales">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <circle cx="9" cy="21" r="1"></circle>
                  <circle cx="20" cy="21" r="1"></circle>
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                </svg>
              </div>
              <div class="settings-item-info">
                <span class="settings-item-label">Paramètres de vente</span>
                <span class="settings-item-value">Autorisations et restrictions</span>
              </div>
            </div>

            <div class="settings-item">
              <div class="settings-item-icon registers">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                </svg>
              </div>
              <div class="settings-item-info">
                <span class="settings-item-label">Gestion des caisses</span>
                <span class="settings-item-value">Limites et configuration</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, onMounted } from 'vue'
import EditCompanyModal from './EditCompanyModal.vue'
import CompanySettingsModal from '@/components/settings/CompanySettingsModal.vue'
import BaseModal from '@/components/base/BaseModal.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import { companyService } from '@/services'
import { useToastStore } from '@/store/modules/toastStore'

export default defineComponent({
  name: 'SettingsView',
  components: {
    EditCompanyModal,
    CompanySettingsModal,
    BaseModal,
    BaseButton
  },
  setup() {
    const toastStore = useToastStore()
    const company = ref(null)
    const loading = ref(true)
    const showEditModal = ref(false)
    const showSettingsModal = ref(false)
    const showLogoUploadModal = ref(false)
    const selectedFile = ref(null)
    const logoPreview = ref(null)
    const isUploadingLogo = ref(false)
    const logoUploadError = ref(null)
    const fileInput = ref(null)

    const loadCompanyFromStorage = () => {
      try {
        loading.value = true
        const stored = localStorage.getItem('company')
        if (stored) {
          company.value = JSON.parse(stored)
        }
      } catch (e) {
        console.error('Erreur lors du chargement des informations de l\'entreprise:', e)
        company.value = null
      } finally {
        loading.value = false
      }
    }

    const editCompany = () => {
      showEditModal.value = true
    }

    const openSettingsModal = () => {
      showSettingsModal.value = true
    }

    const onCompanyUpdated = (updatedCompany) => {
      company.value = updatedCompany
    }

    const onSettingsUpdated = () => {
      // Settings updated, could reload company if needed
    }

    const openLogoUploadModal = () => {
      showLogoUploadModal.value = true
      logoUploadError.value = null
      selectedFile.value = null
      logoPreview.value = null
    }

    const closeLogoUploadModal = () => {
      if (isUploadingLogo.value) return
      showLogoUploadModal.value = false
      selectedFile.value = null
      logoPreview.value = null
      logoUploadError.value = null
      if (fileInput.value) {
        fileInput.value.value = ''
      }
    }

    const handleFileSelect = (event) => {
      const file = event.target.files?.[0]
      if (!file) return

      // Validation de la taille (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        logoUploadError.value = 'Le fichier est trop volumineux. Taille maximale: 2MB'
        selectedFile.value = null
        logoPreview.value = null
        return
      }

      // Validation du type
      if (!file.type.startsWith('image/')) {
        logoUploadError.value = 'Le fichier doit être une image'
        selectedFile.value = null
        logoPreview.value = null
        return
      }

      logoUploadError.value = null
      selectedFile.value = file

      // Créer un aperçu
      const reader = new FileReader()
      reader.onload = (e) => {
        logoPreview.value = e.target.result
      }
      reader.readAsDataURL(file)
    }

    const uploadLogo = async () => {
      if (!selectedFile.value || !company.value?.id) return

      try {
        isUploadingLogo.value = true
        logoUploadError.value = null

        const response = await companyService.uploadLogo(company.value.id, selectedFile.value)
        
        // Mettre à jour le logo dans l'objet company
        if (response.data?.company?.logo_url) {
          company.value.logo_url = response.data.company.logo_url
        } else if (response.data?.logo_url) {
          company.value.logo_url = response.data.logo_url
        }

        // Mettre à jour le localStorage
        localStorage.setItem('company', JSON.stringify(company.value))

        toastStore.addToast({
          type: 'success',
          message: 'Logo mis à jour avec succès',
          duration: 4000
        })

        closeLogoUploadModal()
      } catch (error) {
        console.error('Erreur lors de l\'upload du logo:', error)
        logoUploadError.value = error.response?.data?.message || 'Erreur lors de l\'upload du logo'
        toastStore.addToast({
          type: 'error',
          message: error.response?.data?.message || 'Erreur lors de l\'upload du logo',
          duration: 4000
        })
      } finally {
        isUploadingLogo.value = false
      }
    }

    const getInitials = (name) => {
      if (!name) return '?'
      const parts = name.trim().split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      return d.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: '2-digit'
      })
    }

    const formatCurrency = (amount, currency = 'XOF') => {
      const currencyLabels = {
        'XOF': 'FCFA',
        'EUR': '€',
        'USD': '$',
        'GBP': '£'
      }
      const label = currencyLabels[currency] || currency
      return new Intl.NumberFormat('fr-FR', {
        style: 'decimal',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount || 0) + ' ' + label
    }

    onMounted(() => {
      loadCompanyFromStorage()
    })

    return {
      company,
      loading,
      showEditModal,
      showSettingsModal,
      showLogoUploadModal,
      selectedFile,
      logoPreview,
      isUploadingLogo,
      logoUploadError,
      fileInput,
      editCompany,
      openSettingsModal,
      openLogoUploadModal,
      closeLogoUploadModal,
      handleFileSelect,
      uploadLogo,
      onCompanyUpdated,
      onSettingsUpdated,
      getInitials,
      formatDate,
      formatCurrency
    }
  }
})
</script>

<style scoped>
.settings-view {
  padding: 0 0 24px 0;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  gap: 16px;
}

.header-info {
  flex: 1;
}

.page-heading {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 4px 0;
  letter-spacing: -0.5px;
}

.page-subtitle {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.content-layout {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.card {
  background: white;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 2px solid #e2e8f0;
  background: linear-gradient(135deg, #fafbfc 0%, #f8fafc 100%);
}

.header-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.header-icon svg {
  width: 24px;
  height: 24px;
  stroke: #0A5C6B;
  stroke-width: 2;
}

.header-text {
  flex: 1;
}

.header-text h3 {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 4px 0;
}

.header-text p {
  font-size: 13px;
  color: #64748b;
  margin: 0;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
}

.action-btn svg {
  width: 18px;
  height: 18px;
  stroke-width: 2;
}

.action-btn.secondary {
  background: white;
  color: #475569;
  border: 1.5px solid #e2e8f0;
}

.action-btn.secondary:hover {
  background: #f8fafc;
  border-color: #0A5C6B;
  color: #0A5C6B;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 20px;
  gap: 16px;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid rgba(10, 92, 107, 0.1);
  border-top-color: #0A5C6B;
  border-radius: 50%;
  animation: spin 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-container p {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  gap: 16px;
}

.empty-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-icon svg {
  width: 32px;
  height: 32px;
  stroke: #94a3b8;
  stroke-width: 2;
}

.empty-state h4 {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.empty-state p {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.company-content {
  padding: 24px;
}

.profile-section {
  display: flex;
  align-items: center;
  gap: 20px;
  padding-bottom: 24px;
  border-bottom: 2px solid #e2e8f0;
  margin-bottom: 24px;
}

.profile-avatar {
  width: 72px;
  height: 72px;
  border-radius: 16px;
  background: linear-gradient(135deg, #0A5C6B 0%, #0e7689 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 800;
  box-shadow: 0 4px 12px rgba(10, 92, 107, 0.2);
  flex-shrink: 0;
}

.profile-logo {
  width: 72px;
  height: 72px;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(10, 92, 107, 0.2);
  flex-shrink: 0;
  background: #f8fafc;
}

.profile-logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.logo-container {
  position: relative;
}

.logo-edit-btn {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #0A5C6B;
  color: white;
  border: 2px solid white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  font-size: 12px;
}

.logo-edit-btn:hover {
  background: #094854;
  transform: scale(1.1);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

.logo-edit-btn:active {
  transform: scale(0.95);
}

.profile-info {
  flex: 1;
}

.company-name {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 8px 0;
  letter-spacing: -0.5px;
}

.company-type {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #0A5C6B;
  font-weight: 600;
  margin-bottom: 8px;
}

.company-type svg {
  width: 16px;
  height: 16px;
  stroke-width: 2;
}

.company-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #64748b;
}

.company-meta svg {
  width: 16px;
  height: 16px;
  stroke-width: 2;
}

.description-section {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
}

.description-section .section-header {
  margin-bottom: 12px;
}

.description-text {
  font-size: 14px;
  color: #475569;
  line-height: 1.6;
  margin: 0;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
}

.info-section {
  background: linear-gradient(135deg, #fafbfc 0%, #f8fafc 100%);
  border-radius: 12px;
  padding: 20px;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  font-size: 13px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.section-header svg {
  width: 18px;
  height: 18px;
  stroke-width: 2;
  stroke: #94a3b8;
}

.info-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: white;
  border-radius: 8px;
  margin-bottom: 12px;
  transition: all 0.2s;
}

.info-item:last-child {
  margin-bottom: 0;
}

.info-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.info-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.info-icon svg {
  width: 18px;
  height: 18px;
  stroke-width: 2;
}

.info-icon.phone {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
}

.info-icon.phone svg {
  stroke: #2e7d32;
}

.info-icon.email {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
}

.info-icon.email svg {
  stroke: #1976d2;
}

.info-icon.website {
  background: linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%);
}

.info-icon.website svg {
  stroke: #7b1fa2;
}

.info-icon.address,
.info-icon.city,
.info-icon.country {
  background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
}

.info-icon.address svg,
.info-icon.city svg,
.info-icon.country svg {
  stroke: #e65100;
}

.info-icon.legal {
  background: linear-gradient(135deg, #fce4ec 0%, #f8bbd9 100%);
}

.info-icon.legal svg {
  stroke: #c2185b;
}

.info-icon.capital {
  background: linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 100%);
}

.info-icon.capital svg {
  stroke: #0A5C6B;
}

.info-icon.tax {
  background: linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%);
}

.info-icon.tax svg {
  stroke: #f57f17;
}

.info-icon.currency {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
}

.info-icon.currency svg {
  stroke: #388e3c;
}

.info-icon.balance {
  background: linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 100%);
}

.info-icon.balance svg {
  stroke: #0A5C6B;
}

.info-icon.registers {
  background: linear-gradient(135deg, #ede7f6 0%, #d1c4e9 100%);
}

.info-icon.registers svg {
  stroke: #5e35b1;
}

.info-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-label {
  font-size: 12px;
  color: #94a3b8;
  font-weight: 600;
}

.info-value {
  font-size: 14px;
  color: #0f172a;
  font-weight: 600;
  word-break: break-word;
}

.no-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;
  gap: 12px;
}

.no-info svg {
  width: 32px;
  height: 32px;
  stroke: #cbd5e1;
  stroke-width: 2;
}

.no-info p {
  font-size: 13px;
  color: #94a3b8;
  margin: 0;
}

@media (max-width: 1024px) {
  .info-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .settings-view {
    padding: 0 0 16px 0;
  }

  .card-header {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .header-icon {
    align-self: flex-start;
  }

  .action-btn {
    width: 100%;
    justify-content: center;
  }

  .profile-section {
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }
}

/* Settings Card Styles */
.settings-card .header-icon.settings-icon {
  background: linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%);
}

.settings-card .header-icon.settings-icon svg {
  stroke: #7b1fa2;
}

.settings-content {
  padding: 24px;
}

.settings-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.settings-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: linear-gradient(135deg, #fafbfc 0%, #f8fafc 100%);
  border-radius: 10px;
  transition: all 0.2s;
}

.settings-item:hover {
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
}

.settings-item-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.settings-item-icon svg {
  width: 22px;
  height: 22px;
  stroke-width: 2;
}

.settings-item-icon.sales {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
}

.settings-item-icon.sales svg {
  stroke: #2e7d32;
}

.settings-item-icon.registers {
  background: linear-gradient(135deg, #ede7f6 0%, #d1c4e9 100%);
}

.settings-item-icon.registers svg {
  stroke: #5e35b1;
}

.settings-item-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.settings-item-label {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
}

.settings-item-value {
  font-size: 12px;
  color: #64748b;
}

/* Logo Upload Modal Styles */
.logo-upload-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.logo-preview-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.preview-label {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
}

.preview-container {
  width: 100%;
  min-height: 200px;
  border: 2px dashed #e2e8f0;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  overflow: hidden;
}

.preview-image-wrapper {
  width: 100%;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 8px;
}

.preview-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #94a3b8;
}

.file-input-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.file-input-label {
  display: flex;
  flex-direction: column;
  gap: 8px;
  cursor: pointer;
}

.file-input {
  display: none;
}

.file-input-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  background: #f8fafc;
  border: 2px dashed #cbd5e1;
  border-radius: 8px;
  color: #475569;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
  cursor: pointer;
}

.file-input-button:hover {
  background: #f1f5f9;
  border-color: #0A5C6B;
  color: #0A5C6B;
}

.file-input-button i {
  font-size: 16px;
}

.file-input-hint {
  font-size: 12px;
  color: #64748b;
  margin: 0;
}

.error-message {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #dc2626;
  font-size: 14px;
}

.error-message i {
  font-size: 16px;
}
</style>
